import { createContext, type ReactElement, type ReactNode, useContext, useEffect, useId, useState } from 'react';
import { type GovernedSettingBucketView } from '@vello/api-client';
import {
  type SettingField,
  type SettingFormIssue,
  settingFieldLabelKey,
  settingFieldLabelPath,
  settingFormIssues,
  settingFormSample,
  settingOptionVocabulary,
} from '@vello/setting-forms/form';
import { Button, Checkbox, Cluster, Field, MoneyInput, Notice, RadioGroup, Select, Stack, Switch, Text, TextArea, TextInput } from '@vello/web-theme';
import { countryPackCurrencyAt } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { describeValue, flattenRows, formatUnitNumber, unitScale, valueJson, type ValueRow } from './setting-value';
import { VOCABULARY } from '@vello/country-vocabularies';

/** D883: the codes a vocabulary words, in its order (a text field with `labels` offers them to pick). */
const vocabularyCodes = (id: string): readonly string[] => Object.keys(VOCABULARY.all.find((x) => x.id === id)?.labels ?? {});

/**
 * D775 — the form of a governed key (Configurações › Regras governadas), drawn from the shape the server sends with the key's
 * definition (`@vello/setting-forms`). The operator sees the values that exist — a closed list as choices, a number with its
 * bounds and unit, a list with "Adicionar" — instead of a free JSON box. The words come from `@vello/vocabulary`
 * (`settings.field`, `settings.unit`, the option vocabularies, `settings.form_issue`).
 *
 * The form reports each structural problem under its field and the screen keeps "Propor" off until there is none; the server's
 * validator still decides at proposal and approval.
 */
export interface SettingFormEditorProps {
  readonly settingKey: string;
  readonly form: SettingField;
  readonly value: unknown;
  readonly onValue: (value: unknown) => void;
  /** The bucket being edited (its country gives a money field its currency when no value carries one). */
  readonly bucket: GovernedSettingBucketView | undefined;
  readonly now: string;
}

/** The whole form, so a field deep inside a map finds its words under the form's own path (`levers.*.maxPerTripMinor`). */
const FormRoot = createContext<SettingField | undefined>(undefined);

export function SettingFormEditor({ settingKey, form, value, onValue, bucket, now }: SettingFormEditorProps): ReactElement {
  const { t } = useT();
  const issues = settingFormIssues(form, value);
  const currency = useMoneyCurrency(form, [value, bucket?.current?.value, bucket?.base.value], bucket, now);
  return (
    <FormRoot.Provider value={form}>
      <Stack gap={4}>
        <FieldEditor settingKey={settingKey} field={form} path="" value={value} onChange={onValue} issues={issues} currency={currency} />
        {issues.length > 0 && <Text role="caption" tone="secondary">{t('govset.form.fix')}</Text>}
      </Stack>
    </FormRoot.Provider>
  );
}

interface FieldEditorProps {
  readonly settingKey: string;
  readonly field: SettingField;
  readonly path: string;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  readonly issues: readonly SettingFormIssue[];
  readonly currency: string | undefined;
}

function FieldEditor({ settingKey, field, path, value, onChange, issues, currency }: FieldEditorProps): ReactElement {
  const { t, lang } = useT();
  const { v, has } = useVocab();
  const id = useId();
  const root = useContext(FormRoot);
  const labelPathOf = (p: string): string => (root === undefined ? p : settingFieldLabelPath(root, p));
  const labelKeyAt = (p: string): string => settingFieldLabelKey(settingKey, labelPathOf(p));
  const labelKey = labelKeyAt(path);
  const label = path === '' ? t('govset.editor.value') : has('settings.field', labelKey) ? v('settings.field', labelKey) : lastSegment(path);
  const hint = path !== '' && has('settings.field_hint', labelKey) ? v('settings.field_hint', labelKey) : undefined;
  const own = issues.filter((i) => i.path === path);
  const error = own.length > 0 ? own.map((i) => v('settings.form_issue', i.code)).join(' ') : undefined;
  const optionText = (f: { readonly labels?: string }, code: string): string => {
    const vocab = settingOptionVocabulary(settingKey, labelPathOf(path), f);
    return has(vocab, code) ? v(vocab, code) : code;
  };
  // A count reads as the bare number ("8"), never "8 times".
  const unitText = (unit: string | undefined): string => (unit === undefined || unit === 'count' ? '' : v('settings.unit', unit));
  const words = { v, lang, currency };
  const withHint = (extra: string | undefined): string | undefined => [hint, extra].filter((x) => x !== undefined && x !== '').join(' · ') || undefined;

  switch (field.kind) {
    case 'boolean':
      return (
        <Stack gap={1}>
          <Switch checked={value === true} onChange={(on) => onChange(on)} label={label} />
          {hint !== undefined && <Text role="caption" tone="secondary">{hint}</Text>}
          {error !== undefined && <Text role="caption" tone="danger">{error}</Text>}
        </Stack>
      );
    case 'integer':
    case 'decimal': {
      if (field.kind === 'integer' && field.allowed !== undefined) {
        return (
          <Field label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})} {...(error !== undefined ? { error } : {})}>
            <Select id={id} className="tnum" value={typeof value === 'number' ? String(value) : ''} onChange={(e) => onChange(Number(e.target.value))}>
              <option value="" disabled>{t('govset.form.pick')}</option>
              {field.allowed.map((n) => <option key={n} value={String(n)}>{formatUnitNumber(n, field.unit, words)}</option>)}
            </Select>
          </Field>
        );
      }
      // An amount in minor units is typed as money once the currency is known (never "15000 centavos").
      if (field.kind === 'integer' && field.unit === 'minorUnits' && currency !== undefined) {
        const minor = typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? BigInt(value) : undefined;
        return (
          <MoneyInput
            id={id}
            label={label}
            currency={currency}
            locale={lang}
            valueMinor={minor}
            onChangeMinor={(next) => onChange(next === undefined ? undefined : Number(next))}
            issueText={(issue) => v('format.money_input_issue', issue)}
            {...(withHint(error) !== undefined ? { hint: withHint(error) } : {})}
            maxMinor={BigInt(Math.min(field.max, Number.MAX_SAFE_INTEGER))}
          />
        );
      }
      const scale = unitScale(field.unit);
      // D883: `formatUnitNumber` already says the unit ("1 day(s)"); the range never repeats it ("From 1 day(s) to 365 day(s) day(s)").
      const range = t('govset.form.range', { min: formatUnitNumber(field.min, field.unit, words), max: formatUnitNumber(field.max, field.unit, words), unit: '' }).trim();
      // A percentage, a factor or a coordinate is typed as people say it (10, 1.5, -33.75) and stored in the validator's own step.
      const shown = typeof value === 'number' ? (scale === undefined ? String(value) : String(value / scale.divisor)) : '';
      const typed = (raw: string): number | undefined => {
        if (raw.trim() === '') return undefined;
        const n = Number(raw);
        return scale === undefined ? n : Math.round(n * scale.divisor);
      };
      const suffix = scale !== undefined ? scale.suffix : unitText(field.unit);
      return (
        <Field label={label} htmlFor={id} hint={withHint(range)} {...(error !== undefined ? { error } : {})}>
          <Cluster gap={2} align="center">
            <TextInput
              id={id}
              className="tnum"
              type="number"
              inputMode={field.kind === 'integer' && scale === undefined ? 'numeric' : 'decimal'}
              min={scale === undefined ? field.min : field.min / scale.divisor}
              max={scale === undefined ? field.max : field.max / scale.divisor}
              step={scale !== undefined ? 1 / scale.divisor : field.kind === 'decimal' ? field.step : 1}
              value={shown}
              onChange={(e) => onChange(typed(e.target.value))}
            />
            {suffix !== '' && <Text role="caption" tone="secondary">{suffix}</Text>}
          </Cluster>
        </Field>
      );
    }
    case 'choice':
      if (field.options.length <= 4) {
        return (
          <Field label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})} {...(error !== undefined ? { error } : {})}>
            <RadioGroup name={id} ariaLabel={label} value={typeof value === 'string' ? value : ''} onChange={(next) => onChange(next)} options={field.options.map((o) => ({ value: o, label: optionText(field, o) }))} />
          </Field>
        );
      }
      return (
        <Field label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})} {...(error !== undefined ? { error } : {})}>
          <Select id={id} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)}>
            <option value="" disabled>{t('govset.form.pick')}</option>
            {field.options.map((o) => <option key={o} value={o}>{optionText(field, o)}</option>)}
          </Select>
        </Field>
      );
    case 'choices': {
      const chosen = Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];
      if (field.ordered === true) {
        const rest = field.options.filter((o) => !chosen.includes(o));
        const move = (i: number, d: -1 | 1): void => {
          const next = [...chosen];
          const j = i + d;
          const a = next[i];
          const b = next[j];
          if (a === undefined || b === undefined) return;
          next[i] = b;
          next[j] = a;
          onChange(next);
        };
        return (
          <Field label={label} htmlFor={id} hint={withHint(t('govset.form.ordered'))} {...(error !== undefined ? { error } : {})}>
            <Stack gap={2}>
              <ol className="vello-gr-form-ordered" id={id}>
                {chosen.map((code, i) => (
                  <li key={code}>
                    <Cluster gap={2} align="center" justify="between">
                      <Text>{`${String(i + 1)}. ${optionText(field, code)}`}</Text>
                      <Cluster gap={1}>
                        <Button size="sm" variant="ghost" icon="chevronUp" aria-label={t('govset.form.up', { item: optionText(field, code) })} disabled={i === 0} onClick={() => move(i, -1)} />
                        <Button size="sm" variant="ghost" icon="chevronDown" aria-label={t('govset.form.down', { item: optionText(field, code) })} disabled={i === chosen.length - 1} onClick={() => move(i, 1)} />
                        <Button size="sm" variant="ghost" icon="x" aria-label={t('govset.form.remove.item', { item: optionText(field, code) })} disabled={field.minItems !== undefined && chosen.length <= field.minItems} onClick={() => onChange(chosen.filter((c) => c !== code))} />
                      </Cluster>
                    </Cluster>
                  </li>
                ))}
              </ol>
              {rest.length > 0 && (field.maxItems === undefined || chosen.length < field.maxItems) && (
                <Select aria-label={t('govset.form.addOption')} value="" onChange={(e) => { if (e.target.value !== '') onChange([...chosen, e.target.value]); }}>
                  <option value="">{t('govset.form.addOption')}</option>
                  {rest.map((o) => <option key={o} value={o}>{optionText(field, o)}</option>)}
                </Select>
              )}
            </Stack>
          </Field>
        );
      }
      return (
        <fieldset className="vello-gr-form-group">
          <legend>{label}</legend>
          {hint !== undefined && <Text role="caption" tone="secondary" block>{hint}</Text>}
          <Stack gap={1}>
            {field.options.map((o) => (
              <Checkbox
                key={o}
                label={optionText(field, o)}
                checked={chosen.includes(o)}
                onChange={(on) => onChange(on ? field.options.filter((x) => x === o || chosen.includes(x)) : chosen.filter((x) => x !== o))}
              />
            ))}
          </Stack>
          {error !== undefined && <Text role="caption" tone="danger">{error}</Text>}
        </fieldset>
      );
    }
    case 'text':
      // The digits of an amount (a wallet ceiling) are typed as money once the currency is known.
      if (field.minorAmount === true && currency !== undefined) {
        const digits = typeof value === 'string' && /^\d+$/.test(value) ? BigInt(value) : undefined;
        return (
          <MoneyInput
            id={id}
            label={label}
            currency={currency}
            locale={lang}
            valueMinor={digits}
            onChangeMinor={(next) => onChange(next === undefined ? undefined : next.toString())}
            issueText={(issue) => v('format.money_input_issue', issue)}
            {...(withHint(error) !== undefined ? { hint: withHint(error) } : {})}
            maxMinor={BigInt('9'.repeat(field.maxLength))}
          />
        );
      }
      // D883: a code of an open list a country extends (a document type, an equipment) is picked in words from its vocabulary, never typed as a code; a
      // stored code the vocabulary does not know stays selected and reads as uncatalogued.
      if (field.labels !== undefined) {
        const codes = vocabularyCodes(field.labels);
        const current = typeof value === 'string' ? value : '';
        const listed = current === '' || codes.includes(current) ? codes : [...codes, current];
        return (
          <Field label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})} {...(error !== undefined ? { error } : {})}>
            <Select id={id} value={current} onChange={(e) => onChange(e.target.value)}>
              <option value="" disabled>{t('govset.form.pick')}</option>
              {listed.map((c) => <option key={c} value={c}>{v(field.labels as string, c)}</option>)}
            </Select>
          </Field>
        );
      }
      return (
        <Field label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})} {...(error !== undefined ? { error } : {})}>
          {field.multiline === true
            ? <TextArea id={id} rows={4} maxLength={field.maxLength} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
            : <TextInput id={id} maxLength={field.maxLength} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />}
        </Field>
      );
    case 'money': {
      if (currency === undefined) return <Notice tone="warning">{t('govset.editor.money.noCurrency')}</Notice>;
      const o = isRecord(value) ? value : undefined;
      const minor = typeof o?.amountMinor === 'number' && Number.isSafeInteger(o.amountMinor) ? BigInt(o.amountMinor) : undefined;
      return (
        <MoneyInput
          id={id}
          label={label}
          currency={currency}
          locale={lang}
          valueMinor={minor}
          onChangeMinor={(next) => onChange(next === undefined ? undefined : { amountMinor: Number(next), currency })}
          issueText={(issue) => v('format.money_input_issue', issue)}
          {...(withHint(error) !== undefined ? { hint: withHint(error) } : {})}
          maxMinor={BigInt(field.maxMinor ?? Number.MAX_SAFE_INTEGER)}
        />
      );
    }
    case 'time':
    case 'date':
      return (
        <Field label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})} {...(error !== undefined ? { error } : {})}>
          <TextInput id={id} className="tnum" type={field.kind} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)} />
        </Field>
      );
    case 'object': {
      const o = isRecord(value) ? value : {};
      const set = (name: string, next: unknown): void => {
        const copy: Record<string, unknown> = { ...o };
        if (next === undefined) delete copy[name];
        else copy[name] = next;
        onChange(copy);
      };
      const body = (
        <Stack gap={4}>
          {field.fields.map((f) => {
            const childPath = path === '' ? f.name : `${path}.${f.name}`;
            const child = (
              <FieldEditor key={f.name} settingKey={settingKey} field={f.field} path={childPath} value={o[f.name]} onChange={(next) => set(f.name, next)} issues={issues} currency={currency} />
            );
            if (f.nullable === true) {
              const nullKey = labelKeyAt(childPath);
              const nullLabel = has('settings.field', nullKey) ? v('settings.field', nullKey) : lastSegment(childPath);
              const isNull = o[f.name] === null;
              return (
                <Stack key={f.name} gap={2}>
                  <Checkbox label={t('govset.form.null', { field: inSentence(nullLabel) })} checked={isNull} onChange={(on) => set(f.name, on ? null : settingFormSample(f.field))} />
                  {!isNull && (f.optional !== true || o[f.name] !== undefined) && child}
                </Stack>
              );
            }
            if (f.optional !== true) return child;
            const childKey = labelKeyAt(childPath);
            const childLabel = has('settings.field', childKey) ? v('settings.field', childKey) : lastSegment(childPath);
            const present = o[f.name] !== undefined;
            return (
              <Stack key={f.name} gap={2}>
                <Checkbox label={t('govset.form.use', { field: inSentence(childLabel) })} checked={present} onChange={(on) => set(f.name, on ? settingFormSample(f.field) : undefined)} />
                {present && child}
              </Stack>
            );
          })}
        </Stack>
      );
      if (path === '') return body;
      return (
        <fieldset className="vello-gr-form-group">
          <legend>{label}</legend>
          {hint !== undefined && <Text role="caption" tone="secondary" block>{hint}</Text>}
          {body}
          {error !== undefined && <Text role="caption" tone="danger">{error}</Text>}
        </fieldset>
      );
    }
    case 'list': {
      const items = Array.isArray(value) ? value : [];
      const canAdd = field.maxItems === undefined || items.length < field.maxItems;
      const canRemove = field.minItems === undefined || items.length > field.minItems;
      return (
        <fieldset className="vello-gr-form-group">
          <legend>{label}</legend>
          {hint !== undefined && <Text role="caption" tone="secondary" block>{hint}</Text>}
          <Stack gap={3}>
            {items.length === 0 && <Text role="caption" tone="tertiary">{t('govset.form.empty')}</Text>}
            {items.map((item, i) => (
              <div key={i} className="vello-gr-form-item">
                <Stack gap={2}>
                  <Cluster gap={2} align="center" justify="between">
                    <Text role="caption" tone="secondary">{t('govset.form.item', { n: String(i + 1) })}</Text>
                    <Button size="sm" variant="ghost" icon="x" disabled={!canRemove} onClick={() => onChange(items.filter((_, j) => j !== i))}>{t('govset.form.remove')}</Button>
                  </Cluster>
                  <FieldEditor settingKey={settingKey} field={field.item} path={`${path}[${String(i)}]`} value={item} onChange={(next) => onChange(items.map((x, j) => (j === i ? next : x)))} issues={issues} currency={currency} />
                </Stack>
              </div>
            ))}
            {canAdd && <span><Button size="sm" variant="secondary" onClick={() => onChange([...items, settingFormSample(field.item)])}>{t('govset.form.add')}</Button></span>}
          </Stack>
          {error !== undefined && <Text role="caption" tone="danger">{error}</Text>}
        </fieldset>
      );
    }
    case 'map':
      return <MapEditor settingKey={settingKey} field={field} path={path} label={label} hint={hint} error={error} value={value} onChange={onChange} issues={issues} currency={currency} />;
    case 'json':
      return <JsonField label={label} value={value} onChange={onChange} error={error} />;
  }
}

function MapEditor({ settingKey, field, path, label, hint, error, value, onChange, issues, currency }: {
  settingKey: string;
  field: Extract<SettingField, { kind: 'map' }>;
  path: string;
  label: string;
  hint: string | undefined;
  error: string | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
  issues: readonly SettingFormIssue[];
  currency: string | undefined;
}): ReactElement {
  const { t } = useT();
  const { v, has } = useVocab();
  const [newKey, setNewKey] = useState('');
  const o = isRecord(value) ? value : {};
  const root = useContext(FormRoot);
  const keyVocab = settingOptionVocabulary(settingKey, root === undefined ? path : settingFieldLabelPath(root, path), { ...(field.keyLabels !== undefined ? { labels: field.keyLabels } : {}) });
  const keyText = (k: string): string => (has(keyVocab, k) ? v(keyVocab, k) : k);
  const set = (k: string, next: unknown): void => {
    const copy: Record<string, unknown> = { ...o };
    if (next === undefined) delete copy[k];
    else copy[k] = next;
    onChange(copy);
  };
  const child = (k: string): ReactNode => {
    const editor = <FieldEditor settingKey={settingKey} field={field.value} path={path === '' ? k : `${path}.${k}`} value={o[k]} onChange={(next) => set(k, next)} issues={issues} currency={currency} />;
    if (field.nullableValues !== true) return editor;
    return (
      <Stack gap={2}>
        <Checkbox label={t('govset.form.null', { field: keyText(k) })} checked={o[k] === null} onChange={(on) => set(k, on ? null : settingFormSample(field.value))} />
        {o[k] !== null && editor}
      </Stack>
    );
  };
  return (
    <fieldset className="vello-gr-form-group">
      <legend>{label}</legend>
      {hint !== undefined && <Text role="caption" tone="secondary" block>{hint}</Text>}
      <Stack gap={3}>
        {field.keys !== undefined
          ? field.keys.map((k) => (
            <Stack key={k} gap={2}>
              <Checkbox label={keyText(k)} checked={o[k] !== undefined} onChange={(on) => set(k, on ? settingFormSample(field.value) : undefined)} />
              {o[k] !== undefined && <div className="vello-gr-form-item">{child(k)}</div>}
            </Stack>
          ))
          : (
            <>
              {Object.keys(o).map((k) => (
                <div key={k} className="vello-gr-form-item">
                  <Stack gap={2}>
                    <Cluster gap={2} align="center" justify="between">
                      <Text>{keyText(k)}</Text>
                      <Button size="sm" variant="ghost" icon="x" onClick={() => set(k, undefined)}>{t('govset.form.remove')}</Button>
                    </Cluster>
                    {child(k)}
                  </Stack>
                </div>
              ))}
              <Cluster gap={2} align="end">
                <TextInput aria-label={t('govset.form.newKey')} placeholder={t('govset.form.newKey')} value={newKey} onChange={(e) => setNewKey(e.target.value.trim())} />
                <Button size="sm" variant="secondary" disabled={newKey === '' || o[newKey] !== undefined} onClick={() => { set(newKey, settingFormSample(field.value)); setNewKey(''); }}>{t('govset.form.add')}</Button>
              </Cluster>
            </>
          )}
      </Stack>
      {error !== undefined && <Text role="caption" tone="danger">{error}</Text>}
    </fieldset>
  );
}

/** The escape hatch (a `json` field): the text is parsed on every change and the screen keeps "Propor" off while it does not parse. */
function JsonField({ label, value, onChange, error }: { label: string; value: unknown; onChange: (value: unknown) => void; error: string | undefined }): ReactElement {
  const { t } = useT();
  const id = useId();
  const [text, setText] = useState(() => JSON.stringify(value ?? null, null, 2));
  const [bad, setBad] = useState(false);
  return (
    <Stack gap={2}>
      <Notice tone="warning">{t('govset.form.json')}</Notice>
      <Field label={label} htmlFor={id} {...(bad ? { error: t('govset.editor.json.invalid') } : error !== undefined ? { error } : {})}>
        <TextArea
          id={id}
          rows={6}
          className="tnum"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            try {
              onChange(JSON.parse(e.target.value) as unknown);
              setBad(false);
            } catch {
              onChange(undefined);
              setBad(true);
            }
          }}
        />
      </Field>
    </Stack>
  );
}

/** The currency a money field of this form is typed in: one a value already carries, else the bucket's country pack. */
export function useMoneyCurrency(form: SettingField | undefined, candidates: readonly unknown[], bucket: GovernedSettingBucketView | undefined, now: string): string | undefined {
  const { runtime, scope } = useAdmin();
  const needsMoney = form !== undefined && hasMoney(form);
  const known = needsMoney ? firstCurrency(candidates) : undefined;
  // The bucket's own country, else the country the page is following (a state or a market bucket sits inside it).
  const country = bucket?.scope === 'country' ? bucket.scopeRef : scope.current.country;
  const [pack, setPack] = useState<{ country: string; currency: string | undefined } | undefined>(undefined);
  useEffect(() => {
    if (!needsMoney || known !== undefined || country === undefined || country === '') return undefined;
    let alive = true;
    void (async () => {
      const res = await runtime.config.listCountryPacks(country);
      if (alive) setPack({ country, currency: res.ok ? countryPackCurrencyAt(res.value.packs, now) : undefined });
    })();
    return () => { alive = false; };
  }, [runtime, needsMoney, known, country, now]);
  return known ?? (pack !== undefined && pack.country === country ? pack.currency : undefined);
}

/** Does the form hold an amount: a money field, an integer in minor units, or the digits of an amount? */
export function hasMoney(field: SettingField): boolean {
  switch (field.kind) {
    case 'money': return true;
    case 'integer': return field.unit === 'minorUnits';
    case 'text': return field.minorAmount === true;
    case 'object': return field.fields.some((f) => hasMoney(f.field));
    case 'list': return hasMoney(field.item);
    case 'map': return hasMoney(field.value);
    default: return false;
  }
}

/** The first ISO 4217 code any object with a `currency` inside these values carries. */
function firstCurrency(values: readonly unknown[]): string | undefined {
  const walk = (x: unknown): string | undefined => {
    if (Array.isArray(x)) {
      for (const y of x) {
        const c = walk(y);
        if (c !== undefined) return c;
      }
      return undefined;
    }
    if (!isRecord(x)) return undefined;
    if (typeof x.currency === 'string' && /^[A-Z]{3}$/.test(x.currency)) return x.currency;
    for (const y of Object.values(x)) {
      const c = walk(y);
      if (c !== undefined) return c;
    }
    return undefined;
  };
  for (const v of values) {
    const c = walk(v);
    if (c !== undefined) return c;
  }
  return undefined;
}

/** A label written in the middle of a sentence ("Definir aviso de saldo baixo"): its first capital goes down, except an acronym ("XL", "SMS"). */
const inSentence = (label: string): string => (/^\p{Lu}\p{Ll}/u.test(label) ? `${label.charAt(0).toLowerCase()}${label.slice(1)}` : label);
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const lastSegment = (path: string): string => path.replace(/\[\d+\]/g, '').split('.').pop() ?? path;

/**
 * D775 / D854: a governed value in words, read through its form — the rows of a definition list ("Permitir cancelar: Sim") and the
 * same as one line of text for the places that stay short. A value the form does not describe (no form, or one that does not fit
 * it) has no rows and keeps its JSON text, so nothing is hidden.
 */
export interface SettingValueView {
  readonly text: (value: unknown) => string;
  readonly rows: (value: unknown) => readonly ValueRow[] | undefined;
}

export function useSettingValueView(settingKey: string | undefined, form: SettingField | undefined, currency: string | undefined): SettingValueView {
  const { t, lang } = useT();
  const { v, has } = useVocab();
  const rows = (value: unknown): readonly ValueRow[] | undefined => (settingKey === undefined ? undefined : describeValue(settingKey, form, value, { t, lang, v, has, currency }));
  return {
    rows,
    text: (value) => {
      const r = rows(value);
      return r === undefined ? valueJson(value) : flattenRows(r);
    },
  };
}
